import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Users } from "lucide-react";
import { api } from "../lib/api";
import { Skeleton } from "../components/ui/skeleton";

// 2026-08-28 — small comparison table for execution_router.py's paper
// fan-out (chat: "Before adding real user, would prefer to do some Paper
// Trading for 5 users ... would like to see it in Paper Trade first").
// Originally Candle Breakout only. 2026-09-12 — generalized to cover
// every "Live Orders" stock setup (chat: "Paper Fan out: Apart from CB,
// implement VWAP Bounce Setup B also (or all Live Orders)") via a setup
// selector rather than three near-duplicate pages, since all three
// backend endpoints return the exact same {groups, total_copies} shape
// (see server.py's /candle-breakout, /vwap-bounce and /oi-autopilot
// paper-fanout endpoints — each groups that setup's fan-out collection by
// the shared signal so per-user entry/exit time+price and P&L sit side by
// side for the SAME trade).

const SETUPS = [
  {
    key: "candle-breakout",
    label: "Candle Breakout",
    endpoint: "/candle-breakout/paper-fanout",
    note: "5 seeded paper-test accounts, simulated per-user fills for every real signal.",
  },
  {
    key: "vwap-bounce",
    label: "VWAP Bounce",
    endpoint: "/vwap-bounce/paper-fanout",
    note: "Covers Setups A, B and C — each fan-out copy keeps its own setup tag.",
  },
  {
    key: "oi-autopilot",
    label: "OI Autopilot",
    endpoint: "/oi-autopilot/paper-fanout",
    note: "5 seeded paper-test accounts, simulated per-user fills for every real signal.",
  },
];

const fmt = (n, d = 2) =>
  n == null ? "—" : Number(n).toLocaleString("en-IN", { maximumFractionDigits: d });

const pctColor = (v) => (v == null ? "var(--text-muted-2)" : v >= 0 ? "var(--bull)" : "var(--bear)");

const fmtIST = (iso) => {
  if (!iso) return "—";
  try {
    let s = String(iso);
    if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) s = s + "Z";
    const d = new Date(s);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleTimeString("en-GB", {
      timeZone: "Asia/Kolkata", hour12: false,
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch { return "—"; }
};

const STATUS_STYLE = {
  OPEN: { color: "var(--info, #4b8bbf)", label: "Open" },
  CLOSED: { color: "var(--muted-3)", label: "Closed" },
};

const CloseReasonPill = ({ reason }) => {
  if (!reason) return <span className="text-muted-3">—</span>;
  const color = reason === "STOP_LOSS" ? "var(--bear)"
    : reason === "PROFIT_BOOKED" ? "var(--bull)"
    : "var(--muted-2)";
  return <span style={{ color }}>{reason.replace(/_/g, " ")}</span>;
};

const SetupTypePill = ({ setupType }) => {
  if (!setupType) return null;
  return (
    <span
      className="text-[10px] uppercase tracking-wider font-mono-data px-1.5 py-0.5 border"
      style={{ color: "var(--text-muted-2)", borderColor: "var(--grid)" }}
    >
      Setup {setupType}
    </span>
  );
};

const GroupCard = ({ group }) => {
  const users = [...group.users].sort((a, b) =>
    String(a.user_name || a.user_id).localeCompare(String(b.user_name || b.user_id))
  );
  const withPnl = users.filter((u) => u.pnl_pct != null);
  const spread = withPnl.length > 1
    ? Math.max(...withPnl.map((u) => u.pnl_pct)) - Math.min(...withPnl.map((u) => u.pnl_pct))
    : null;

  return (
    <div className="border border-grid" data-testid="fanout-group-card">
      <div className="px-4 py-2.5 border-b border-grid flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <span className="font-heading text-lg font-semibold">{group.symbol}</span>
          <SetupTypePill setupType={group.setup_type} />
          <span className="text-xs text-muted-2 font-mono-data">
            {group.option_type} {fmt(group.atm_strike, 0)} · slot {group.slot}
          </span>
        </div>
        {spread != null && (
          <span className="text-[10px] uppercase tracking-wider text-muted-3 font-mono-data">
            P&amp;L spread across users: <span style={{ color: pctColor(spread) }}>{fmt(spread)} pts</span>
          </span>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-muted-3 uppercase tracking-wider text-[10px] font-mono-data border-b border-grid">
              <th className="px-3 py-2">User</th>
              <th className="px-3 py-2 text-right">Entry Time (IST)</th>
              <th className="px-3 py-2 text-right">Entry Price</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2 text-right">Exit Time (IST)</th>
              <th className="px-3 py-2 text-right">Exit Price</th>
              <th className="px-3 py-2">Close Reason</th>
              <th className="px-3 py-2 text-right">P&amp;L %</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const st = STATUS_STYLE[u.status] || STATUS_STYLE.OPEN;
              const isClosed = u.status === "CLOSED";
              return (
                <tr key={u.id} className="border-b border-grid/40" data-testid="fanout-user-row">
                  <td className="px-3 py-2 font-semibold">{u.user_name || u.user_id}</td>
                  <td className="px-3 py-2 text-right text-muted-2 font-mono-data">{fmtIST(u.entered_at)}</td>
                  <td className="px-3 py-2 text-right text-muted-1 font-mono-data">{fmt(u.atm_ce_ltp)}</td>
                  <td className="px-3 py-2">
                    <span className="text-[10px] uppercase tracking-wider font-mono-data" style={{ color: st.color }}>
                      {st.label}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right text-muted-2 font-mono-data">
                    {isClosed ? fmtIST(u.exit_time) : "—"}
                  </td>
                  <td className="px-3 py-2 text-right text-muted-1 font-mono-data">
                    {isClosed ? fmt(u.current_ce_ltp) : "—"}
                  </td>
                  <td className="px-3 py-2 text-[11px]">
                    <CloseReasonPill reason={u.close_reason} />
                  </td>
                  <td className="px-3 py-2 text-right font-semibold" style={{ color: pctColor(u.pnl_pct) }}>
                    {u.pnl_pct == null ? "—" : `${u.pnl_pct >= 0 ? "+" : ""}${fmt(u.pnl_pct)}%`}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const PaperFanoutComparison = () => {
  const [setupKey, setSetupKey] = useState(SETUPS[0].key);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(2);

  const setup = SETUPS.find((s) => s.key === setupKey) || SETUPS[0];

  const load = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get(setup.endpoint, { params: { days } });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to load ${setup.label} paper fan-out comparison`);
    } finally {
      setLoading(false);
    }
  }, [setup.endpoint, setup.label, days]);

  useEffect(() => {
    setData(null);
    load();
    const t = setInterval(() => load(true), 20000);
    return () => clearInterval(t);
  }, [load]);

  const groups = data?.groups || [];

  return (
    <section className="space-y-4" data-testid="paper-fanout-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-muted-3 font-mono-data mb-1">
            {setup.label} · Multi-User Paper Test
          </div>
          <h2 className="font-heading text-2xl flex items-center gap-2">
            <Users size={20} /> Paper Fan-Out Comparison
          </h2>
          <div className="text-[10px] text-muted-3 font-mono-data mt-1">
            {setup.note} · not counted in Journal/Reports ·{" "}
            {data ? `${data.total_copies} copies, ${groups.length} signals` : ""}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={setupKey}
            onChange={(e) => setSetupKey(e.target.value)}
            className="bg-transparent border border-grid px-2 py-1.5 text-xs font-mono-data"
            data-testid="fanout-setup-select"
          >
            {SETUPS.map((s) => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </select>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="bg-transparent border border-grid px-2 py-1.5 text-xs font-mono-data"
            data-testid="fanout-days-select"
          >
            <option value={1}>Today</option>
            <option value={2}>Last 2 days</option>
            <option value={7}>Last 7 days</option>
          </select>
          <button
            onClick={() => load()}
            className="px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] font-mono-data border flex items-center gap-1.5"
            style={{ color: "var(--text-muted-2)", borderColor: "var(--grid)" }}
            data-testid="fanout-refresh"
          >
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      </div>

      {loading && !data ? (
        <div className="space-y-2">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : groups.length === 0 ? (
        <div className="border border-grid px-4 py-8 text-center text-xs text-muted-2 font-mono-data">
          No paper fan-out data yet for {setup.label}. Run <code>seed_paper_test_users.py</code> once on the VPS
          (creates the 5 test accounts), then wait for the next {setup.label} auto-entry —
          each real signal automatically clones into 5 simulated per-user fills here.
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map((g) => (
            <GroupCard key={`${setupKey}::${g.slot}::${g.symbol}::${g.setup_type || ""}`} group={g} />
          ))}
        </div>
      )}
    </section>
  );
};

export default PaperFanoutComparison;
